import { defineStore } from 'pinia'
import { cast } from '@broker-as/acquisit-ui-vue/functions'

export interface PendingTask {
	progress: number | null
}

export interface QueuedBackup {
	fn: () => Promise<void>
	resolve: () => void
	reject: (error: unknown) => void
}

export interface PendingTasksStoreState {
	pendingCount: number
	tasks: Record<number, PendingTask>
	nextTaskId: number
	queuedBackupFn: (() => Promise<void>) | null
	queuedBackupPromise: Promise<void> | null
	queuedBackupResolve: (() => void) | null
	whenIdleWaiters: (() => void)[]
	backupInFlight: boolean
	backupQueue: QueuedBackup[]
}

const clampProgress = (progress: number): number => Math.min(1, Math.max(0, progress))

export const usePendingTasksStore = defineStore('pending-tasks', {
	state: () => cast<PendingTasksStoreState>({
		pendingCount: 0,
		tasks: {},
		nextTaskId: 1,
		queuedBackupFn: null,
		queuedBackupPromise: null,
		queuedBackupResolve: null,
		whenIdleWaiters: [],
		backupInFlight: false,
		backupQueue: []
	}),
	
	getters: {
		hasPendingTasks: state => state.pendingCount > 0,
		
		aggregateProgress(state): number | null {
			if (state.pendingCount === 0) {
				return null
			}
			
			const tasks = Object.values(state.tasks)
			let hasReportedProgress = false
			let sum = 0
			
			for (const task of tasks) {
				if (task.progress !== null) {
					hasReportedProgress = true
					sum += task.progress
				}
			}
			
			if (!hasReportedProgress) {
				return null
			}
			
			return sum / state.pendingCount
		},
		
		isIndeterminate(): boolean {
			return this.hasPendingTasks && this.aggregateProgress === null
		}
	},
	
	actions: {
		isFullyIdle(): boolean {
			return this.pendingCount === 0
				&& !this.queuedBackupFn
				&& !this.backupInFlight
				&& this.backupQueue.length === 0
		},
		
		beginTask(): { taskId: number, setProgress: (progress: number) => void } {
			const taskId = this.nextTaskId++
			
			this.tasks[taskId] = { progress: null }
			this.pendingCount++
			
			return {
				taskId,
				setProgress: (progress: number) => {
					if (this.tasks[taskId]) {
						this.tasks[taskId].progress = clampProgress(progress)
					}
				}
			}
		},
		
		endTask(taskId: number) {
			if (this.tasks[taskId]) {
				delete this.tasks[taskId]
			}
			
			this.pendingCount = Math.max(0, this.pendingCount - 1)
			this.flushQueuedBackup()
			
			if (this.pendingCount === 0) {
				this.resolveWhenIdleWaiters()
			}
		},
		
		resolveWhenIdleWaiters() {
			if (!this.isFullyIdle()) {
				return
			}
			
			const waiters = this.whenIdleWaiters
			this.whenIdleWaiters = []
			
			for (const resolve of waiters) {
				resolve()
			}
		},
		
		registerTask<T>(promise: Promise<T>): Promise<T> {
			const { taskId } = this.beginTask()
			
			return promise.finally(() => {
				this.endTask(taskId)
			})
		},
		
		registerTaskWithProgress<T>(
			run: (setProgress: (progress: number) => void) => Promise<T>
		): Promise<T> {
			const { taskId, setProgress } = this.beginTask()
			
			return run(setProgress).finally(() => {
				this.endTask(taskId)
			})
		},
		
		runBackup(fn: () => Promise<void>): Promise<void> {
			this.backupInFlight = true
			
			return fn().finally(() => {
				this.backupInFlight = false
				this.processBackupQueue()
			})
		},
		
		processBackupQueue() {
			if (this.backupQueue.length > 0) {
				const { fn, resolve, reject } = this.backupQueue.shift()!
				
				this.runBackup(fn).then(resolve, reject)
				return
			}
			
			if (this.pendingCount === 0 && this.queuedBackupFn) {
				this.flushQueuedBackup()
				return
			}
			
			this.resolveWhenIdleWaiters()
		},
		
		queueBackup(fn: () => Promise<void>): Promise<void> {
			if (this.pendingCount > 0) {
				this.queuedBackupFn = fn
				
				if (!this.queuedBackupPromise) {
					this.queuedBackupPromise = new Promise(resolve => {
						this.queuedBackupResolve = resolve
					})
				}
				
				return this.queuedBackupPromise
			}
			
			if (this.backupInFlight) {
				return new Promise<void>((resolve, reject) => {
					this.backupQueue.push({ fn, resolve, reject })
				})
			}
			
			return this.runBackup(fn)
		},
		
		whenIdle(): Promise<void> {
			if (this.isFullyIdle()) {
				return Promise.resolve()
			}
			
			return new Promise(resolve => {
				this.whenIdleWaiters.push(resolve)
				
				if (this.isFullyIdle()) {
					const index = this.whenIdleWaiters.indexOf(resolve)
					
					if (index !== -1) {
						this.whenIdleWaiters.splice(index, 1)
					}
					
					resolve()
				}
			})
		},
		
		flushQueuedBackup() {
			if (this.pendingCount > 0 || !this.queuedBackupFn) {
				return
			}
			
			const fn = this.queuedBackupFn
			const resolve = this.queuedBackupResolve
			
			this.queuedBackupFn = null
			this.queuedBackupPromise = null
			this.queuedBackupResolve = null
			
			void this.runBackup(fn).finally(() => {
				resolve?.()
			})
		}
	}
})
